import type { AnyFormApi } from '@tanstack/react-form';
import { useLayoutEffect, useRef } from 'react';

export function toggleUndefined<T>(value: T | undefined, initial: T) {
  if (value !== undefined) {
    return undefined;
  }

  return initial;
}

/** Trim a string and collapse an empty result to `null` (for optional API fields). */
export const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/** Collapse an empty array to `null` (for optional API list fields). */
export const arrayToNull = (value: string[]): string[] | null => (value.length > 0 ? value : null);

/**
 * The first validation message from a field's `meta.errors`. Field-group fields
 * type their errors as `never` (validation lives on the parent form), so this
 * narrows at runtime rather than relying on the field's static error type.
 */
export const fieldErrorMessage = (errors: readonly unknown[]): string | undefined => {
  const first = errors[0];
  if (typeof first === 'object' && first !== null && 'message' in first) {
    const { message } = first as { message: unknown };
    return typeof message === 'string' ? message : undefined;
  }
  return undefined;
};

export const useApplyDraftFormValues = <T extends object>(formApi: AnyFormApi, draftState?: T) => {
  const hasAppliedDraft = useRef(false);
  useLayoutEffect(() => {
    if (!draftState || hasAppliedDraft.current) {
      return;
    }
    hasAppliedDraft.current = true;

    // use generic type due to Object.entries
    for (const [field, value] of Object.entries(draftState)) {
      formApi.setFieldValue(field, value, { dontRunListeners: true });
    }
  }, [draftState, formApi]);
};
